/**
 * Bootstrap hook for prefetching Agents data after login/home mount
 * 
 * Goal: Prefetch the first page of Agents data in the background so that
 * when the user clicks on the Agents menu item, the data is already in cache.
 * 
 * This provides a "big CRM" experience where the Agents page appears almost instantly.
 * 
 * Usage:
 * ```tsx
 * const { company } = useParams<{ company: string }>();
 * const { client } = useClient();
 * 
 * useBootstrapAgentsData({
 *   companyId: company,
 *   enabled: !!client && !!company,
 * });
 * ```
 */

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { agentKeys } from "../lib/queryKeys";
import { getAgents } from "../api/agentsApi";
import { DEFAULT_AGENT_QUERY_PARAMS } from "./agentQueryDefaults";

type UseBootstrapAgentsDataProps = {
  companyId: string | undefined;
  enabled?: boolean; // default true
};

export function useBootstrapAgentsData({ 
  companyId, 
  enabled = true 
}: UseBootstrapAgentsDataProps) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    if (!companyId) return;

    // Use the EXACT same default params as the Agents page initial query
    // This ensures the query keys match and cached data is immediately available
    const prefetchParams = { ...DEFAULT_AGENT_QUERY_PARAMS };
    const queryKey = agentKeys.list(companyId, prefetchParams);

    // Check if data already exists in cache (before prefetching)
    const existingData = queryClient.getQueryData(queryKey);
    if (existingData) {
      return;
    }

    // Prefetch in background - don't await, let it run silently
    queryClient.prefetchQuery({
      queryKey,
      queryFn: async () => {
        const queryParams: any = {
          page: prefetchParams.page || 1,
          limit: prefetchParams.limit || 25,
        };
        if (prefetchParams.search) queryParams.search = prefetchParams.search;
        if (prefetchParams.status) queryParams.status = prefetchParams.status;
        if (prefetchParams.role) queryParams.role = prefetchParams.role;
        if (prefetchParams.sort) queryParams.sort = prefetchParams.sort;

        const result = await getAgents(companyId, queryParams);
        const agentsArray = Array.isArray(result.agents) ? result.agents : (Array.isArray(result) ? result : []);
        const total = result.total !== undefined ? result.total : agentsArray.length;

        return {
          agents: agentsArray,
          pagination: {
            page: prefetchParams.page || DEFAULT_AGENT_QUERY_PARAMS.page || 1,
            limit: prefetchParams.limit || DEFAULT_AGENT_QUERY_PARAMS.limit || 25,
            total,
            pages: Math.ceil(total / (prefetchParams.limit || DEFAULT_AGENT_QUERY_PARAMS.limit || 25)),
            hasMore: (prefetchParams.page || 1) * (prefetchParams.limit || 25) < total,
          },
        };
      },
      // Use same caching strategy as the actual query (must match useAgentsQuery)
      // Use queryOptions.heavyTable preset: 2min stale, 60min cache
      staleTime: 2 * 60 * 1000, // 2 minutes - matches queryOptions.heavyTable
      gcTime: 60 * 60 * 1000, // 60 minutes - matches queryOptions.heavyTable
    }).catch((error) => {
      // Silently handle errors - prefetch failures shouldn't break the app
    });
  }, [companyId, enabled, queryClient]);
}

